import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import AnimatedBackground from '../components/AnimatedBackground';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { MapPin, Mail, Clock, Send, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ContactPage = () => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubjectChange = (value) => {
    setFormData(prev => ({ ...prev, subject: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Send form data to PHP handler
      const response = await fetch('/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitting(false);
        setIsSubmitted(true);
        toast.success(result.message || t('contact.form.successMessage'));
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            company: '',
            email: '',
            phone: '',
            subject: '',
            message: '',
          });
        }, 3000);
      } else {
        throw new Error(result.error || t('contact.form.errorMessage'));
      }
    } catch (error) {
      setIsSubmitting(false);
      // Log error only in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Form submission error:', error);
      }
      toast.error(error.message || t('contact.form.errorMessage'));
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-[#1E2A38] overflow-hidden">
        <AnimatedBackground className="opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1E2A38]/50 to-[#1E2A38]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-white/70 text-lg max-w-2xl">
              {t('contact.subtitle')}
            </p>
            <div className="w-24 h-1 bg-[#0066CC] rounded-full mt-8" />
          </ScrollReveal>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Form */}
            <ScrollReveal className="lg:col-span-3">
              <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-sm hover:shadow-lg transition-shadow duration-300">
                {isSubmitted ? (
                  <div className="text-center py-12 animate-fadeIn">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center animate-scaleIn">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#2B3A4D] mb-2">
                      {t('contact.form.successTitle')}
                    </h3>
                    <p className="text-[#6B7B8C]">
                      {t('contact.form.successMessage')}
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Name */}
                      <div className="space-y-2">
                        <Label 
                          htmlFor="name" 
                          className={`text-[#2B3A4D] transition-colors duration-200 ${focusedField === 'name' ? 'text-[#0066CC]' : ''}`}
                        >
                          {t('contact.form.name')} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          onFocus={() => setFocusedField('name')}
                          onBlur={() => setFocusedField(null)}
                          required
                          className="border-[#E5E7EB] focus:border-[#0066CC] focus:ring-[#0066CC]/20 transition-all duration-200"
                        />
                      </div>
                      
                      {/* Company */}
                      <div className="space-y-2">
                        <Label 
                          htmlFor="company" 
                          className={`text-[#2B3A4D] transition-colors duration-200 ${focusedField === 'company' ? 'text-[#0066CC]' : ''}`}
                        >
                          {t('contact.form.company')} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          onFocus={() => setFocusedField('company')}
                          onBlur={() => setFocusedField(null)}
                          required
                          className="border-[#E5E7EB] focus:border-[#0066CC] focus:ring-[#0066CC]/20 transition-all duration-200"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Email */}
                      <div className="space-y-2">
                        <Label 
                          htmlFor="email" 
                          className={`text-[#2B3A4D] transition-colors duration-200 ${focusedField === 'email' ? 'text-[#0066CC]' : ''}`}
                        >
                          {t('contact.form.email')} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          onFocus={() => setFocusedField('email')}
                          onBlur={() => setFocusedField(null)}
                          required
                          className="border-[#E5E7EB] focus:border-[#0066CC] focus:ring-[#0066CC]/20 transition-all duration-200"
                        />
                      </div>
                      
                      {/* Phone */}
                      <div className="space-y-2">
                        <Label 
                          htmlFor="phone" 
                          className={`text-[#2B3A4D] transition-colors duration-200 ${focusedField === 'phone' ? 'text-[#0066CC]' : ''}`}
                        >
                          {t('contact.form.phone')}
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          onFocus={() => setFocusedField('phone')}
                          onBlur={() => setFocusedField(null)}
                          className="border-[#E5E7EB] focus:border-[#0066CC] focus:ring-[#0066CC]/20 transition-all duration-200"
                        />
                      </div>
                    </div>
                    
                    {/* Subject */}
                    <div className="space-y-2">
                      <Label 
                        htmlFor="subject" 
                        className={`text-[#2B3A4D] transition-colors duration-200 ${focusedField === 'subject' ? 'text-[#0066CC]' : ''}`}
                      >
                        {t('contact.form.subject')} <span className="text-red-500">*</span>
                      </Label>
                      <Select 
                        value={formData.subject} 
                        onValueChange={handleSubjectChange}
                        onOpenChange={(open) => setFocusedField(open ? 'subject' : null)}
                      >
                        <SelectTrigger className="border-[#E5E7EB] focus:ring-[#0066CC]/20 transition-all duration-200">
                          <SelectValue placeholder="Select a subject" />
                        </SelectTrigger>
                        <SelectContent>
                          {t('contact.form.subjectOptions').map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Message */}
                    <div className="space-y-2">
                      <Label 
                        htmlFor="message" 
                        className={`text-[#2B3A4D] transition-colors duration-200 ${focusedField === 'message' ? 'text-[#0066CC]' : ''}`}
                      >
                        {t('contact.form.message')} <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        onFocus={() => setFocusedField('message')}
                        onBlur={() => setFocusedField(null)}
                        required
                        rows={6}
                        className="border-[#E5E7EB] focus:border-[#0066CC] focus:ring-[#0066CC]/20 resize-none transition-all duration-200"
                      />
                    </div>
                    
                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#0066CC] hover:bg-[#0055AA] text-white py-6 text-base group transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#0066CC]/30 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          {t('contact.form.submit')}
                          <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200" />
                        </span>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </ScrollReveal>
            
            {/* Contact Information */}
            <ScrollReveal delay={200} className="lg:col-span-2">
              <div className="bg-[#2B3A4D] rounded-2xl p-8 lg:p-10 text-white h-full relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 opacity-10">
                  <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <pattern id="contactGrid" width="40" height="40" patternUnits="userSpaceOnUse">
                        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/>
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#contactGrid)" />
                  </svg>
                </div>
                
                <div className="relative z-10">
                  <h2 className="text-2xl font-bold mb-8">
                    {t('contact.info.title')}
                  </h2>
                  
                  <div className="space-y-8">
                    {/* Address */}
                    <div className="flex items-start gap-4 group">
                      <div className="w-12 h-12 rounded-xl bg-[#0066CC]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0066CC] transition-all duration-300 group-hover:scale-110">
                        <MapPin className="w-6 h-6 text-[#0066CC] group-hover:text-white transition-colors duration-300" />
                      </div>
                      <div>
                        <h3 className="font-medium mb-1">Address</h3>
                        <p className="text-white/70">{t('contact.info.address')}</p>
                      </div>
                    </div>
                    
                    {/* Email */}
                    <div className="flex items-start gap-4 group">
                      <div className="w-12 h-12 rounded-xl bg-[#0066CC]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0066CC] transition-all duration-300 group-hover:scale-110">
                        <Mail className="w-6 h-6 text-[#0066CC] group-hover:text-white transition-colors duration-300" />
                      </div>
                      <div>
                        <h3 className="font-medium mb-1">Email</h3>
                        <a 
                          href="mailto:contact@hitech-group.io" 
                          className="text-white/70 hover:text-white transition-colors duration-200 relative inline-block group/link"
                        >
                          {t('contact.info.email')}
                          <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0066CC] group-hover/link:w-full transition-all duration-300" />
                        </a>
                      </div>
                    </div>
                    
                    {/* Hours */}
                    <div className="flex items-start gap-4 group">
                      <div className="w-12 h-12 rounded-xl bg-[#0066CC]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0066CC] transition-all duration-300 group-hover:scale-110">
                        <Clock className="w-6 h-6 text-[#0066CC] group-hover:text-white transition-colors duration-300" />
                      </div>
                      <div>
                        <h3 className="font-medium mb-1">Business Hours</h3>
                        <p className="text-white/70">{t('contact.info.hours')}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Corporate Statement */}
                  <div className="mt-12 pt-8 border-t border-white/10">
                    <p className="text-white/60 text-sm leading-relaxed">
                      HITECH GROUP is a wholly owned subsidiary of Global Services s.r.o., an international technology group.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
